import { openapiClient, unwrapResult } from '@/api/openapi-client';
import { useRestaurantStore } from '@/stores/restaurant.store';
import type { CreateRefundBody, Payment, RefundEntry } from '@restaurant/api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { orderKeys } from './queryKeys';

export function useAcceptProof() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ orderId }: { orderId: string }) =>
      unwrapResult<Payment>(
        await openapiClient.POST('/api/restaurants/{restaurantId}/orders/{orderId}/proof/accept', {
          params: { path: { restaurantId, orderId } },
        }),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orderKeys.all(restaurantId) });
    },
  });
}

export function useRejectProof() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ orderId, reason }: { orderId: string; reason: string }) =>
      unwrapResult<Payment>(
        await openapiClient.POST('/api/restaurants/{restaurantId}/orders/{orderId}/proof/reject', {
          params: { path: { restaurantId, orderId } },
          body: { reason },
        }),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orderKeys.all(restaurantId) });
    },
  });
}

export function useMarkCollected() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ orderId }: { orderId: string }) =>
      unwrapResult<Payment>(
        await openapiClient.POST(
          '/api/restaurants/{restaurantId}/orders/{orderId}/payment/collect',
          { params: { path: { restaurantId, orderId } } },
        ),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orderKeys.all(restaurantId) });
    },
  });
}

export function useCreateRefund() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      orderId,
      amountCents,
      reason,
    }: {
      orderId: string;
      amountCents: number;
      reason: CreateRefundBody['reason'];
    }) =>
      unwrapResult<RefundEntry>(
        await openapiClient.POST('/api/restaurants/{restaurantId}/orders/{orderId}/refunds', {
          params: { path: { restaurantId, orderId } },
          body: { amountCents, reason },
        }),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orderKeys.all(restaurantId) });
    },
  });
}

// Confirms an out-of-band refund (Paycat / cash / manual, recorded PENDING) has
// been paid out → flips it SETTLED. PayPal refunds are already SETTLED, so this
// is only surfaced for PENDING rows.
export function useSettleRefund() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ orderId, refundId }: { orderId: string; refundId: string }) =>
      unwrapResult<RefundEntry>(
        await openapiClient.POST(
          '/api/restaurants/{restaurantId}/orders/{orderId}/refunds/{refundId}/settle',
          { params: { path: { restaurantId, orderId, refundId } } },
        ),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orderKeys.all(restaurantId) });
    },
  });
}
